"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { fetchEmail } from "@/redux/actions/userActions";
import { useAppDispatch, useAppSelector } from "@/redux/store";

type AuthContextValue = {
  isAuthenticated: boolean;
  isChecking: boolean;
  user: Record<string, unknown>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const authenticator = useAppSelector((s) => s.authenticator);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await dispatch(fetchEmail());
      } finally {
        setIsChecking(false);
      }
    };
    void checkAuth();
  }, [dispatch]);

  const value = useMemo<AuthContextValue>(() => {
    const unauthorized = Boolean(authenticator.user?.unauthorized);
    return {
      isAuthenticated: !unauthorized,
      isChecking,
      user: authenticator.user,
    };
  }, [authenticator.user, isChecking]);

  /*
   * Always render `children`. Returning `null` while checking auth unmounts the full app
   * (sidebar + route pages) and mounts it again when `fetchEmail` completes — that reads as
   * a double load on Search History and other routes.
   */
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
